<template>
  <Teleport to="body">
    <dialog v-if="store.account?.nameIntroductionPending || stage === 'done'" ref="dialog" class="name-dialog" aria-labelledby="name-introduction-title" @cancel.prevent="cancel">
      <div class="name-card">
        <template v-if="stage === 'name'">
          <h2 id="name-introduction-title">{{ $t('copy.d82e5fe24eed') }}</h2>
          <p>{{ $t('copy.ad7dc4d97744') }}</p>
          <form class="stack" @submit.prevent="finish(name)">
            <div class="field"><label for="introduced-name">{{ $t('copy.6191e66c9324') }}</label><input id="introduced-name" ref="nameInput" v-model="name" class="input" autocomplete="nickname" maxlength="80" required :disabled="busy"/></div>
            <p v-if="error" class="error" role="alert">{{ error }}</p>
            <div class="name-actions"><button type="button" class="btn btn-quiet" :disabled="busy" @click="finish(null)">{{ $t('copy.0e01af8bfa3f') }}</button><button class="btn btn-primary" :disabled="busy || !name.trim()">{{ busy ? $t('copy.23e39291d613') : $t('copy.bfc9ad16532c') }}</button></div>
          </form>
        </template>
        <template v-else>
          <h2 id="name-introduction-title" ref="confirmationHeading" tabindex="-1">{{ $t('copy.dee7fc14c8b3') }} {{ store.account?.displayName }}.</h2>
          <p>{{ $t('copy.50456ed74228') }} <RouterLink to="/account" @click="close">{{ $t('copy.806002b61abc') }}</RouterLink>.</p>
          <div class="name-actions"><button type="button" class="btn btn-primary" @click="continueJourney">{{ $t('copy.9d7fd0e0bd0a') }}</button></div>
          <p class="next-trails"><RouterLink to="/trips/new" @click="close">{{ $t('copy.c2e229142148') }}</RouterLink><RouterLink to="/account" @click="close">{{ $t('copy.12be53daf719') }}</RouterLink></p>
        </template>
      </div>
    </dialog>
  </Teleport>
</template>

<script setup lang="ts">
import { t } from '@/i18n'

import { nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useTripStore } from '@/stores/trips'
import { api, json } from '@/services/api'
import { localReturnPath } from '@/services/gearOperations'
import type { Account } from '@/types/api'

const store = useTripStore(), route = useRoute(), router = useRouter()
const dialog = ref<HTMLDialogElement>(), nameInput = ref<HTMLInputElement>(), confirmationHeading = ref<HTMLElement>()
const stage = ref<'name' | 'done'>('name'), name = ref('Camper'), busy = ref(false), error = ref('')
let activeAccount = '', returnFocus: HTMLElement | null = null
// Let authentication and explicit trip-merge continuations settle first. Email's
// restored page is a stable signed-in destination and can show the introduction.
const waitingForAuthentication = () => ['/access/check', '/access/open', '/account/continue'].includes(route.path)
watch([() => store.account?.id, () => store.account?.nameIntroductionPending, () => store.account?.hasVerifiedEmail, () => route.path], async () => {
  const account = store.account
  if (activeAccount && activeAccount !== account?.id) close()
  if (activeAccount && stage.value === 'done') return
  if (!account?.nameIntroductionPending || !account.hasVerifiedEmail || waitingForAuthentication()) {
    if (dialog.value?.open) close()
    return
  }
  await nextTick()
  if (store.account?.id !== account.id || !store.account.nameIntroductionPending || waitingForAuthentication() || dialog.value?.open) return
  activeAccount = account.id
  stage.value = 'name'
  name.value = account.displayName
  error.value = ''
  returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
  dialog.value?.showModal()
  nameInput.value?.focus()
  nameInput.value?.select()
}, { immediate: true, flush: 'post' })

async function finish(displayName: string | null) {
  if (busy.value || !activeAccount) return
  const accountId = activeAccount
  busy.value = true
  error.value = ''
  try {
    const account = await api<Account>('/api/account/name-introduction', json('POST', { displayName }))
    if (store.account?.id !== accountId) return
    stage.value = 'done'
    store.account = account
    if (store.profile) store.profile = { ...store.profile, displayName: account.displayName }
    await nextTick()
    confirmationHeading.value?.focus()
  } catch (e: any) {
    if (store.account?.id === accountId) error.value = e.message || t('copy.44a4c9520230')
  } finally { busy.value = false }
}
function close() {
  dialog.value?.close()
  activeAccount = ''
  stage.value = 'name'
  const target = returnFocus?.isConnected && returnFocus !== document.body ? returnFocus : document.querySelector<HTMLElement>('.account-link')
  target?.focus()
}
function cancel() { if (!busy.value) { if (stage.value === 'name') void finish(null); else close() } }
async function continueJourney() {
  close()
  if (route.path === '/access/restored') await router.push(localReturnPath(route.query.next, localReturnPath(localStorage.getItem('camplist.account.returnTo'))))
}
</script>

<style scoped>
.name-dialog{background:transparent;border:0;padding:0;width:560px;max-width:calc(100vw - 28px);max-height:calc(100dvh - 28px);overflow:auto}.name-dialog::backdrop{background:#172720aa;backdrop-filter:blur(3px)}.name-card{background:#fffefb;border:1px solid var(--sand);border-radius:20px;padding:26px;line-height:1.6;box-shadow:0 22px 65px #17272044}.name-card h2{margin-top:0;overflow-wrap:anywhere}.name-actions{display:flex;flex-wrap:wrap;gap:10px;justify-content:flex-end}.next-trails{display:flex;gap:18px;flex-wrap:wrap;margin-bottom:0}.name-card a:focus-visible{outline:2px solid var(--forest);outline-offset:3px}@media(max-width:480px){.name-card{padding:21px}.name-actions{display:grid}.name-actions .btn{width:100%}}
</style>
